<template>
  <div class="__tailwind">
    <Popover as-child>
      <template #trigger>
        <Button
          variant="outline"
          label="Find and Replace"
          class="gap-2 text-sm"
          data-dd="Bulk Edit: Find and Replace Button"
        >
          <template #icon-left>
            <Search class="h-4 w-4" />
          </template>
        </Button>
      </template>
      <template #content>
        <div class="flex flex-col gap-2">
          <Input v-model="stringToFind" aria-label="Find" placeholder="Find" />
          <Input
            v-model="replacementString"
            aria-label="Replace"
            placeholder="Replace"
          />
          <Select
            v-model="selectedCol"
            aria-label="Column"
            class="mt-3"
            :options="columnOptions"
            placeholder="Select column"
          />
        </div>
        <SuccessButton
          class="mt-4"
          data-dd="Bulk Edit: Replace All Button"
          @click="replaceAll"
        >
          Replace All
        </SuccessButton>
        <Text variant="body-sm" class="mt-2 text-muted-foreground text-sm">
          Warning: This action is irreversible.
        </Text>
      </template>
    </Popover>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import SuccessButton from "@/components/CMSComponents/Buttons/SuccessButton.vue";
import Button from "@/cms/primitives/Button/Button.vue";
import Input from "@/cms/inputs/Input/Input.vue";
import Select from "@/cms/inputs/Select/Select.vue";
import Text from "@/cms/primitives/Text/Text.vue";
import Popover from "@/cms/components/Popover/Popover.vue";
import { Search } from "lucide-vue-next";

const stringToFind = ref("");
const replacementString = ref("");
const selectedCol = ref(null);
const store = useStore();
const route = useRoute();

const categoryID = computed(() => route.params.categoryID);
const category = computed(() =>
  store.getters["api/category"](categoryID.value)
);
const entryData = computed(() => category.value?.entries);

const getAlternativeName = (name) => {
  const altName = store.getters.getStoreVar(
    "Category",
    "page",
    "text",
    `${name}AlternativeName`
  );
  return altName || name;
};

const columnOptions = computed(() => [
  { value: null, label: "All columns" },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  {
    value: "graduationDate",
    label: getAlternativeName("graduationDate"),
  },
  { value: "eventDate", label: getAlternativeName("eventDate") },
  { value: "activity", label: getAlternativeName("activity") },
  { value: "type", label: getAlternativeName("type") },
  { value: "description", label: "Description" },
]);

const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replaceAll = async () => {
  if (!stringToFind.value && !replacementString.value) return;

  const fields = selectedCol.value
    ? [selectedCol.value]
    : columnOptions.value
        .map((option) => option.value)
        .filter((option) => option);

  const updates = entryData.value?.reduce((acc, entry) => {
    const update = {};
    fields.forEach((field) => {
      // When stringToFind is not empty
      if (stringToFind.value) {
        // Escape special regex characters in stringToFind
        const escapedString = escapeRegExp(stringToFind.value);
        const regex = new RegExp(escapedString, "gi");
        if (entry[field]?.match(regex)) {
          // Replace matching entries with replacementString (which may be empty)
          update[field] = entry[field]?.replace(regex, replacementString.value);
        }
      }
      // When stringToFind is empty, replace only empty fields with replacementString
      else if (stringToFind.value === "" && entry[field] === "") {
        update[field] = replacementString.value;
      }
    });
    if (Object.keys(update).length) {
      acc.push({
        categoryID: categoryID.value,
        path: entry.path,
        entryID: entry.path.split("/")[2],
        update,
      });
    }
    return acc;
  }, []);
  if (updates.length) {
    await store.dispatch("api/patchEntriesBulk", {
      updates,
      opts: { updateStoreFirst: true },
    });
  }
};
</script>
